import { useEffect } from 'react';

import { checkIfSubdomain, portNumberAsterisk, removeAsterisks, stripProtocol, subDomainAsterisk } from '../utils/csp';

const { tycoonHosts, quasarUiHosts } = __appConfig.csp;

const allowedMessagesHosts = [...tycoonHosts, ...quasarUiHosts];

type Handler<T> = (data: T) => void;

interface ICustomMessage {
    type: 'navigation' | 'sprav_dialogs_inited' | 'dialogs_account-linking_start' | 'dialogs_debug-info';
    payload: { path: string; applicationName: string; debugInfo: { logs: string[] } };
}

export const onMessageConstructor =
    <T>(handler: Handler<T>, allowedHosts: string[]): Exclude<WindowEventHandlers['onmessage'], null> =>
    (event) => {
        const origin = new URL(event.origin);

        const acceptMessage = allowedHosts.some((host) => {
            // Если содержит звезду
            if (host.match(subDomainAsterisk) !== null) {
                const preparedHost = removeAsterisks(host);
                const [allowedHost, protocol] = stripProtocol(preparedHost);

                return origin.protocol.startsWith(protocol) && checkIfSubdomain(origin.host, allowedHost);
            }

            if (host.match(portNumberAsterisk) !== null) {
                const preparedHost = removeAsterisks(host);
                const [allowedHost, protocol] = stripProtocol(preparedHost);

                return origin.protocol.startsWith(protocol) && origin.host === allowedHost;
            }

            const [allowedHost, protocol] = stripProtocol(host);

            return origin.protocol.startsWith(protocol) && origin.host === allowedHost;
        });

        if (!acceptMessage) {
            return;
        }

        try {
            const data = JSON.parse(event.data);

            if (data.hasOwnProperty('type')) {
                handler(data);
            }
        } catch (e) {
            console.error(e);
        }
    };

export const useMessageSubscription = <T = ICustomMessage>(handler: Handler<T>) => {
    useEffect(() => {
        const listener = onMessageConstructor(handler, allowedMessagesHosts);

        window.addEventListener('message', listener);

        return () => {
            window.removeEventListener('message', listener);
        };
    }, [handler]);
};
